/**
 * Consumption chart component that displays water consumption data over time.
 * Features interactive date range selection and chart updates.
 */

import { Line } from "react-chartjs-2";
import PropTypes from "prop-types";
import { chartOptions } from "../chartConfig";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import fi from "date-fns/locale/fi";
import enUS from "date-fns/locale/en-US";

/**
 * ConsumptionChart component that renders a line chart of water consumption data.
 * Allows users to select date ranges and update the chart view.
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Chart data object containing datasets
 * @param {Object} props.chartRef - Reference to the chart instance
 * @param {Function} props.onDateChange - Callback function for date range changes
 * @returns {JSX.Element} The rendered ConsumptionChart component
 */
export const ConsumptionChart = ({ data, chartRef, onDateChange }) => {
  const { t, i18n } = useTranslation();
  const [dateRange, setDateRange] = useState({
    rangeStart: "",
    rangeEnd: "",
  });

  /**
   * Handles changes to date input fields
   * @param {string} type - The type of date input ('rangeStart' or 'rangeEnd')
   * @param {string} value - The new date value
   */
  const handleDateInputChange = (type, value) => {
    setDateRange((prev) => ({ ...prev, [type]: value }));
  };

  /**
   * Updates the chart based on selected date range
   * Validates date inputs and updates chart options
   */
  const handleUpdateChart = () => {
    if (!dateRange.rangeStart || !dateRange.rangeEnd) {
      alert(t("select_both_dates"));
      return;
    }

    const startDate = new Date(dateRange.rangeStart);
    const endDate = new Date(dateRange.rangeEnd);

    if (startDate > endDate) {
      alert(t("start_date_after_end"));
      return;
    }

    // Call parent's onDateChange with the date range
    onDateChange("range", {
      start: dateRange.rangeStart,
      end: dateRange.rangeEnd,
    });

    // Update chart options for the x-axis
    if (chartRef.current) {
      const chart = chartRef.current;
      chart.options.scales.x.min = startDate;
      chart.options.scales.x.max = endDate;

      // Reset the y-axis
      chart.options.scales.y.min = 0;
      chart.options.scales.y1.min = 0;

      // Force a full update
      chart.reset();
      chart.update("none");
    }
  };

  // Localize chart options
  const localizedOptions = useMemo(() => {
    return {
      ...chartOptions,
      adapters: { date: { locale: i18n.language === "fi" ? fi : enUS } },
      plugins: {
        ...chartOptions.plugins,
        title: {
          ...chartOptions.plugins.title,
          text: t("water_consumption"),
        },
      },
      scales: {
        ...chartOptions.scales,
        x: {
          ...chartOptions.scales.x,
          title: { ...chartOptions.scales.x.title, text: t("date") },
        },
        y: {
          ...chartOptions.scales.y,
          title: { ...chartOptions.scales.y.title, text: t("average_30min") },
        },
        y1: {
          ...chartOptions.scales.y1,
          title: { ...chartOptions.scales.y1.title, text: t("max_limit") },
        },
      },
    };
  }, [t, i18n.language]);

  // Localize dataset labels
  const modifiedData = useMemo(() => {
    return {
      ...data,
      datasets: data.datasets.map((dataset) => ({
        ...dataset,
        label:
          dataset.label === "Average (30min)"
            ? t("average_30min")
            : dataset.label === "Water consumption threshold"
            ? t("water_consumption_threshold")
            : dataset.label,
        pointRadius: 2,
        pointHoverRadius: 4,
      })),
    };
  }, [data, t]);

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 w-full">
      <div className="gap-4 mt-4 items-center justify-end">
        <div className="h-[400px] w-full">
          <Line ref={chartRef} options={localizedOptions} data={modifiedData} />
        </div>

        <div className="w-full flex flex-col md:flex-row mt-4 items-start md:items-center md:w-auto gap-2">
          <label className="text-sm font-medium text-gray-700">
            {t("date_range")}
          </label>
          <div className="flex flex-col md:flex-row w-full md:w-auto gap-2">
            <input
              type="date"
              value={dateRange.rangeStart}
              onChange={(e) =>
                handleDateInputChange("rangeStart", e.target.value)
              }
              className="border rounded px-3 py-2 w-full md:w-auto"
              placeholder={t("start_date")}
            />
            <input
              type="date"
              value={dateRange.rangeEnd}
              onChange={(e) =>
                handleDateInputChange("rangeEnd", e.target.value)
              }
              className="border rounded px-3 py-2 w-full md:w-auto"
              placeholder={t("end_date")}
            />
            <button
              onClick={handleUpdateChart}
              className="bg-[#cabb78] hover:bg-[#b5a666] text-white px-4 py-2 rounded w-full md:w-auto"
            >
              {t("update_chart")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// PropTypes validation for ConsumptionChart component
ConsumptionChart.propTypes = {
  data: PropTypes.shape({
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        data: PropTypes.arrayOf(
          PropTypes.shape({
            x: PropTypes.instanceOf(Date),
            y: PropTypes.number,
          })
        ),
        borderColor: PropTypes.string,
        yAxisID: PropTypes.string,
      })
    ),
  }).isRequired,
  chartRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]).isRequired,
  onReset: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
};

export default ConsumptionChart;
